import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function BrandStory() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-32 bg-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div
            className={`relative transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="/images/pingpai.png"
                  alt="民间艺人工艺"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-warm-gold/20 rounded-2xl -z-10" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-deep-lake/20 rounded-2xl -z-10" />
            </div>

            <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-deep-lake flex items-center justify-center flex-shrink-0">
                  <Quote className="w-5 h-5 text-warm-gold" />
                </div>
                <div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-2">
                    "每一支浮漂都承载着我们对钓鱼的热爱，对品质的执着。"
                  </p>
                  <p className="text-deep-lake font-medium text-sm">—— 创始人寄语</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <span
              className={`inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              Brand Story
            </span>
            <h2
              className={`text-3xl md:text-4xl lg:text-5xl font-serif-zh font-bold text-deep-lake mb-6 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '300ms' }}
            >
              品牌故事
            </h2>
            <div
              className={`space-y-4 text-gray-600 leading-relaxed transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <p>
                在长江之畔、洞庭之滨的湖南临湘，这片被誉为"中国钓具（浮标）之乡"的土地，渔猎文化绵延千年，自古便有"折苇作标，窥水寻鱼"的古法垂钓智慧。千百年间，无数民间手艺人守着一盏孤灯，精雕细琢每一支浮漂，用指尖温度赋予芦苇、纳米材料生命，让小小的浮漂成为连接钓者与水域的无声信使。
              </p>
              <p>
                时光流转到2012年，一群深耕垂钓领域、痴迷浮漂工艺的匠人，怀揣着对垂钓运动的赤诚热爱，也怀揣着传承民间浮漂技艺的初心，在临湘创立了民间艺人品牌。彼时，浮漂市场良莠不齐，要么是粗制滥造的低端产品，灵敏度差、耐用性低；要么是高价舶来品，脱离本土钓友的实际垂钓需求。创始人团队深知，钓友们需要的不是冰冷的渔具，而是懂水情、懂鱼情、更懂人心的"垂钓伙伴"，于是立下誓言：做有温度的浮漂，守一辈子的匠心，让每一位钓友都能用上专业、靠谱、有灵魂的浮漂。
              </p>
            </div>

            <div
              className={`grid grid-cols-3 gap-6 mt-8 mb-8 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '500ms' }}
            >
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-deep-lake mb-1">2012</div>
                <div className="text-sm text-gray-500">品牌创立</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-deep-lake mb-1">10+</div>
                <div className="text-sm text-gray-500">年匠心传承</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-deep-lake mb-1">1000万+</div>
                <div className="text-sm text-gray-500">钓友信赖</div>
              </div>
            </div>

            <Link
              to="/about"
              className={`inline-flex items-center gap-2 px-6 py-3 bg-deep-lake text-white font-medium rounded-lg hover:bg-deep-lake-light transition-all duration-300 group ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '600ms' }}
            >
              了解更多故事
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
